// 2026-05-26: shared helpers for rendering market titles consistently
// across the app. Single source of truth — extracted from CopytradingDashboard
// where these lived inline. Used by:
//   • /copytrading        (Recent Trades)
//   • /portfolio?tab=activity (RecentActivitySection)
//   • /portfolio (PositionsSection)
//
// Why this lives in one place: Polymarket markets all have long
// English-question wrappers ("Will the highest temperature in NYC be
// between 82-83°F on May 26?") that occupy horizontal space without
// adding signal. Stripping the wrapper, and preferring the canonical
// `event_title` / prettified `event_slug` for multi-outcome events,
// keeps the trade rows scannable. Doing it in 3 different components
// = guaranteed drift, so this helper owns the rule.

const SLUG_TITLE_KEEP_UPPER = new Set([
  'nyc', 'la', 'usa', 'uk', 'eu', 'btc', 'eth', 'sol', 'us', 'uae',
  'fbi', 'cia', 'nba', 'nfl', 'mlb', 'nhl', 'ai', 'ufc', 'wwe', 'lol',
]);
const SLUG_TITLE_KEEP_LOWER = new Set([
  'in', 'on', 'at', 'a', 'an', 'the', 'of', 'and', 'or', 'to',
  'for', 'by', 'vs',
]);

/** Title-case a kebab-cased event_slug, keeping initialisms uppercase
 *  and stopwords lowercase (except as first word).
 *  Example: `highest-temperature-in-nyc-may-26-2026`
 *        → `Highest Temperature in NYC May 26 2026` */
export function prettifyEventSlug(slug: string): string {
  return slug
    .split('-')
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (SLUG_TITLE_KEEP_UPPER.has(lower)) return lower.toUpperCase();
      if (i > 0 && SLUG_TITLE_KEEP_LOWER.has(lower)) return lower;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');
}

/** Strip the "Will the …?" wrapper from a Polymarket yes/no question.
 *  Falls back to the original string if no prefix matched. */
export function stripQuestionPrefix(q: string): string {
  return q
    .replace(/^(Will the |Will |Is the |Is |What will be the |What is the |Which |How many |Does the |Will\s)/i, '')
    .replace(/\?$/, '')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

export interface MarketTitleInput {
  isMultiOutcome?: boolean | null;
  /** Canonical short title from Polymarket gamma (multi-outcome only). */
  eventTitle?: string | null;
  /** Kebab-cased event slug — fallback for multi-outcome when no event_title. */
  eventSlug?: string | null;
  /** Raw market question with the "Will the …?" wrapper. */
  marketQuestion?: string | null;
  /** Per-sub-market variant ("Map 1 Winner", "82-83°F"). For multi-outcome the
   *  question is "<event> - <groupItemTitle>" (gamma), so stripping this suffix
   *  yields the clean event line ("Counter-Strike: ENCE vs Entropy"). */
  groupItemTitle?: string | null;
  /** Full outcomes pair from the market. For sports/esports moneyline these are
   *  the team names (["CHAOS", "MANA eSports"]), which Polymarket renders as the
   *  matchup heading — so we show "CHAOS vs MANA eSports", same as their page. */
  outcomes?: (string | null)[] | null;
}

// Outcome labels that are NOT team names (so we don't render "Yes vs No").
const NON_TEAM_OUTCOME = /^(yes|no|over|under|up|down)$/i;

/** Resolve the display title for a market row.
 *  Priority:
 *    1. multi-outcome: market_question with the "- <groupItemTitle>" suffix
 *       stripped → the clean event line ("Counter-Strike: ENCE vs Entropy",
 *       from gamma question "…ENCE vs Entropy - Map 1 Winner")
 *    2. multi-outcome canonical event_title (Polymarket gamma)
 *    3. multi-outcome prettified event_slug
 *    4. market_question with the "Will the …?" wrapper stripped
 *    5. fallback string
 *
 *  Pure function — no React, no i18n. Safe to use in any render path. */
export function marketDisplayTitle(input: MarketTitleInput, fallback: string): string {
  const q = input.marketQuestion?.trim();
  // 0. Sports/esports matchup: the two outcomes are the team names
  //    (["CHAOS","MANA eSports"]) → "CHAOS vs MANA eSports", exactly what
  //    Polymarket's page shows. Checked BEFORE the isMultiOutcome gate — sports
  //    positions often arrive with isMultiOutcome=false (no event_slug), but the
  //    outcomes pair is enough. Skipped when outcomes are Yes/No/Over/Under
  //    (totals/handicap sub-markets aren't a team matchup).
  const oc = (input.outcomes || []).map(o => (o || '').trim()).filter(Boolean);
  if (oc.length === 2 && !NON_TEAM_OUTCOME.test(oc[0]) && !NON_TEAM_OUTCOME.test(oc[1])) {
    return `${oc[0]} vs ${oc[1]}`;
  }
  // Totals sub-market (outcomes Over/Under): show the MATCH, not the bet
  // question. The Over/Under detail already renders in the outcome chip
  // ("O/U 2.5 Games"), so the title should carry the matchup. We fall through
  // to the matchup extraction below (eventTitle "{Sport}: {A} vs {B} - …"),
  // and only land on the question if no match is available.
  // Sports matchup fallback (Activity feed, where outcomes are cached as
  // Yes/No so the branch above can't fire): the matchup is embedded in the
  // title with gamma's fixed format "{Sport}: {A} vs {B} [- variant |
  // (BOx) - tournament]". Trim at the first " - "/" ("/" •" to recover
  // "{Sport}: {A} vs {B}". Gated on " vs " so it only touches sports rows;
  // Positions/Recent-Trades already returned above via real team outcomes.
  const matchupSrc = (input.eventTitle && input.eventTitle.trim()) || q;
  if (matchupSrc && / vs /i.test(matchupSrc)) {
    const cut = matchupSrc.split(/ - | \(| • | · | \| /)[0].trim();
    if (cut && / vs /i.test(cut)) return cut;
  }
  if (input.isMultiOutcome) {
    // 1. Strip the sub-market variant suffix from the question (real gamma
    //    relation: question = "<event> - <groupItemTitle>").
    const git = input.groupItemTitle?.trim();
    if (q && git) {
      const suffix = ` - ${git}`;
      if (q.endsWith(suffix)) {
        const base = q.slice(0, -suffix.length).trim();
        if (base) return base;
      }
    }
    if (input.eventTitle && input.eventTitle.trim()) return input.eventTitle;
    if (input.eventSlug && input.eventSlug.trim()) return prettifyEventSlug(input.eventSlug);
  }
  if (!q) return fallback;
  return stripQuestionPrefix(q);
}
